const SUPPORT_PORTAL_ROLES = new Set([
  'ROLE_ADMIN',
  'ROLE_SUB_ADMIN',
  'ROLE_APP_MANAGER',
  'ROLE_DEVELOPER',
  'ROLE_MANAGEMENT',
  'ROLE_THERAPY_SUP_L1',
  'ROLE_THERAPY_SUP_L2',
  'ROLE_THERAPY_SUP_L3',
  'ROLE_THERAPIST_ADMIN',
  'ROLE_CLINIC_SUCCESS_MANAGER',
  'ROLE_CLINIC_SUCCESS_ADMIN',
  'ROLE_CLINIC_ROSTER_MANAGER',
  'ROLE_CLINIC_ROSTER_ADMIN',
  'ROLE_VC_AUDIT_ADMIN',
  'ROLE_VC_AUDIT_MANAGER',
  'ROLE_DOCSIDE_ADMIN',
  'ROLE_DOCSIDE_MANAGER',
  'ROLE_SUPPORT',
  'ROLE_SUPPORT_USER',
  'ROLE_PAYMENT_SUPPORT',
  'ROLE_READ_ONLY_RESTRICTED',
])

const MENTOR_PORTAL_ROLES = new Set([
  'ROLE_DOCTOR',
  'ROLE_TEACHER',
  'ROLE_THERAPIST',
  'ROLE_SCREENER',
])

const MENTOR_CLINIC_ACCESS_ROLES = new Set([
  'ROLE_DOCTOR',
  'ROLE_THERAPIST',
  'ROLE_SCREENER',
])

const CLINIC_PORTAL_ROLES = new Set([
  'ROLE_CLINIC_OWNER',
  'ROLE_CLINIC_MANAGER',
  'ROLE_CLINIC_ADMIN',
  'ROLE_CLINIC_PROSPECT',
  'ROLE_PHARMACIST',
])

const BUSINESS_PORTAL_ROLES = new Set([
  'ROLE_BUSINESS_CUSTOMER',
  'ROLE_BUSINESS_PROSPECT',
])

// Patient portal — see docs/PATIENT_PORTAL_PLAN.md.
// Note: ROLE_USER (generic consumer) is intentionally NOT in this set. Only
// users explicitly granted ROLE_PATIENT route to /patient-home. This keeps the
// existing consumer flow unchanged for non-patient ROLE_USER accounts.
const PATIENT_PORTAL_ROLES = new Set(['ROLE_PATIENT'])
const PATIENT_PROSPECT_PORTAL_ROLES = new Set(['ROLE_PATIENT_PROSPECT'])
const WELLNESS_PORTAL_ROLES = new Set(['ROLE_USER', 'ROLE_PROFESSIONAL_USER'])

// Applicant/prospect roles that should make the portal chooser visible but
// must not silently default the user into a gate page. ROLE_CREATOR is the
// mentor applicant role; the actual practitioner default starts at ROLE_TEACHER
// or clinical roles in MENTOR_PORTAL_ROLES.
const CHOOSER_ONLY_PORTAL_ROLES = new Set(['ROLE_CREATOR'])

const ROLE_BASED_PORTAL_PREFERENCES = [
  {
    portalId: 'mentor',
    roles: MENTOR_PORTAL_ROLES,
  },
  {
    portalId: 'clinic',
    roles: new Set([
      'ROLE_CLINIC_OWNER',
      'ROLE_CLINIC_MANAGER',
      'ROLE_CLINIC_ADMIN',
      'ROLE_PHARMACIST',
      'ROLE_CLINIC_PROSPECT',
    ]),
  },
  {
    portalId: 'support',
    roles: SUPPORT_PORTAL_ROLES,
  },
  {
    portalId: 'business',
    roles: BUSINESS_PORTAL_ROLES,
  },
  {
    portalId: 'patient',
    roles: PATIENT_PORTAL_ROLES,
  },
  {
    portalId: 'patient-prospect',
    roles: PATIENT_PROSPECT_PORTAL_ROLES,
  },
]

const DEFAULT_PORTAL_DEFINITIONS = [
  {
    id: 'support',
    roles: SUPPORT_PORTAL_ROLES,
    path: '/support-home',
  },
  {
    id: 'mentor',
    roles: MENTOR_PORTAL_ROLES,
    path: (userRoles) =>
      hasAnyRole(userRoles, MENTOR_CLINIC_ACCESS_ROLES)
        ? '/mentor-home/my-clinic-associations'
        : '/mentor-home/preferences-and-settings',
  },
  {
    id: 'clinic',
    roles: CLINIC_PORTAL_ROLES,
    path: '/clinic-home',
  },
  {
    id: 'business',
    roles: BUSINESS_PORTAL_ROLES,
    path: '/workplace',
  },
  {
    id: 'patient',
    roles: PATIENT_PORTAL_ROLES,
    path: '/patient-home',
  },
  {
    id: 'patient-prospect',
    roles: PATIENT_PROSPECT_PORTAL_ROLES,
    path: '/care/status',
  },
  {
    id: 'clinic-assistant',
    roles: new Set(['ROLE_CLINIC_ASSISTANT']),
    path: '/ca-home',
  },
  {
    id: 'kiosk',
    roles: new Set(['ROLE_KIOSK', 'ROLE_AUMHUM_SCREEN']),
    path: '/kiosk',
  },
  {
    id: 'display',
    roles: new Set(['ROLE_OOH']),
    path: '/display',
  },
]

function hasAnyRole(userRoles, candidateRoles) {
  return userRoles.some((role) => candidateRoles.has(role))
}

function resolvePortalPath(portal, userRoles) {
  return typeof portal.path === 'function' ? portal.path(userRoles) : portal.path
}

function normalizeRedirectPath(path) {
  return typeof path === 'string' && path.trim() ? path.trim() : ''
}

export function isWellnessRedirectPath(path) {
  const redirectPath = normalizeRedirectPath(path)
  if (!redirectPath) return false

  const pathname = redirectPath.split(/[?#]/)[0]
  return (
    pathname === '/' ||
    pathname === '/wellness' ||
    pathname.startsWith('/wellness/') ||
    pathname === '/onboarding' ||
    pathname.startsWith('/onboarding/')
  )
}

function getDefaultPortalMatches(userRoles) {
  return DEFAULT_PORTAL_DEFINITIONS.filter((portal) => hasAnyRole(userRoles, portal.roles))
}

export function resolveRoleBasedPreferredPortalId(authoritiesLike, options = {}) {
  const userRoles = normalizeAuthorities(authoritiesLike)
  if (userRoles.length === 0) return null

  const availablePortalIds = Array.isArray(options?.availablePortalIds)
    ? new Set(options.availablePortalIds.filter((portalId) => typeof portalId === 'string'))
    : new Set(getDefaultPortalMatches(userRoles).map((portal) => portal.id))

  for (const preference of ROLE_BASED_PORTAL_PREFERENCES) {
    if (
      availablePortalIds.has(preference.portalId) &&
      hasAnyRole(userRoles, preference.roles)
    ) {
      return preference.portalId
    }
  }

  return null
}

function resolvePairedKioskPreference(matches, options = {}) {
  const hasClinicAssistantPortal = matches.some((portal) => portal.id === 'clinic-assistant')
  const hasKioskPortal = matches.some((portal) => portal.id === 'kiosk')

  if (!hasClinicAssistantPortal || !hasKioskPortal || matches.length !== 2) {
    return null
  }

  const lastChosenFlow =
    typeof options?.lastChosenFlow === 'string' ? options.lastChosenFlow.trim() : ''
  const kioskConfig = options?.kioskConfig || null
  const roleCaConfig = options?.roleCaConfig || null

  // Prefer the last-used flow, else fall back to whichever portal has a saved
  // config present. Flow is implied by the portal now, so we no longer key off
  // a FLOW_ID field — just the presence of a stored config.
  if (lastChosenFlow === 'kiosk' || kioskConfig) {
    return { portalId: 'kiosk', path: '/kiosk' }
  }

  if (lastChosenFlow === 'clinic-assistant' || roleCaConfig) {
    return { portalId: 'clinic-assistant', path: '/ca-home' }
  }

  return null
}

function parseJsonSafe(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return null
  }

  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

export function normalizeAuthorities(authoritiesLike) {
  if (Array.isArray(authoritiesLike)) {
    return authoritiesLike.filter((value) => typeof value === 'string' && value.trim())
  }

  if (typeof authoritiesLike === 'string') {
    const parsedAuthorities = parseJsonSafe(authoritiesLike)
    return Array.isArray(parsedAuthorities)
      ? parsedAuthorities.filter((value) => typeof value === 'string' && value.trim())
      : []
  }

  return []
}

export function resolveDirectPortalHandoff(authoritiesLike, options = {}) {
  const resolvedHome = resolveRoleBasedPortalHome(authoritiesLike, options)

  if (
    !resolvedHome ||
    resolvedHome.portalId === 'consumer' ||
    resolvedHome.portalId === 'portal-chooser'
  ) {
    return null
  }

  return resolvedHome
}

export function resolveRoleBasedPortalHome(authoritiesLike, options = {}) {
  const userRoles = normalizeAuthorities(authoritiesLike)

  if (userRoles.length === 0) {
    return null
  }

  const defaultPortalMatches = getDefaultPortalMatches(userRoles)
  const pairedKioskPreference = resolvePairedKioskPreference(defaultPortalMatches, options)

  if (pairedKioskPreference) {
    return pairedKioskPreference
  }

  if (defaultPortalMatches.length === 1) {
    const portal = defaultPortalMatches[0]
    return { portalId: portal.id, path: resolvePortalPath(portal, userRoles) }
  }

  if (defaultPortalMatches.length > 1 || hasAnyRole(userRoles, CHOOSER_ONLY_PORTAL_ROLES)) {
    return { portalId: 'portal-chooser', path: '/portals' }
  }

  const hasWellnessPortalRole = hasAnyRole(userRoles, WELLNESS_PORTAL_ROLES)
  const hasOnlyWellnessRoles = userRoles.every((role) => WELLNESS_PORTAL_ROLES.has(role))

  if (hasWellnessPortalRole && hasOnlyWellnessRoles) {
    return { portalId: 'consumer', path: '/wellness' }
  }

  if (!hasOnlyWellnessRoles) {
    return { portalId: 'portal-chooser', path: '/portals' }
  }

  return null
}

export function resolvePostLoginNavigation(authoritiesLike, options = {}) {
  const roleBasedDestination =
    resolveRoleBasedPortalHome(authoritiesLike, options) ||
    { portalId: 'portal-chooser', path: '/portals' }
  const redirectPath = normalizeRedirectPath(options?.redirectPath)

  if (
    redirectPath &&
    (!isWellnessRedirectPath(redirectPath) || roleBasedDestination.portalId === 'consumer')
  ) {
    return { portalId: 'redirect', path: redirectPath }
  }

  return roleBasedDestination
}

export function hasNonWellnessPortalRole(authoritiesLike) {
  const userRoles = normalizeAuthorities(authoritiesLike)
  if (userRoles.length === 0) return false
  return (
    getDefaultPortalMatches(userRoles).length > 0 ||
    hasAnyRole(userRoles, CHOOSER_ONLY_PORTAL_ROLES) ||
    userRoles.some((role) => !WELLNESS_PORTAL_ROLES.has(role))
  )
}

export function rememberPortalHandoff(portalId) {
  if (typeof window === 'undefined' || typeof portalId !== 'string' || !portalId.trim()) {
    return
  }

  sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')

  if (portalId === 'consumer' || portalId === 'wellness') {
    sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
    localStorage.removeItem('lastChosenFlow')
    return
  }

  if (portalId === 'portal-chooser' || portalId === 'redirect') {
    return
  }

  localStorage.setItem('lastChosenFlow', portalId)
}

export function preparePostLoginPortalState(options = {}) {
  if (typeof window === 'undefined') {
    return
  }

  const explicitWellness = options?.explicitWellness === true

  try {
    sessionStorage.removeItem('chosenSomeFlowInThisSession')
    sessionStorage.removeItem('portalAutoNavigatedToAtFlow')

    if (explicitWellness) {
      sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
    } else {
      sessionStorage.removeItem('explicitlyChoseConsumerPortal')
    }
  } catch {
    // Storage may be blocked; routing still falls back to role-based decisions.
  }

  try {
    const lastChosenFlow = localStorage.getItem('lastChosenFlow')
    if (lastChosenFlow === 'consumer' || lastChosenFlow === 'wellness') {
      localStorage.removeItem('lastChosenFlow')
    }
  } catch {
    // Non-fatal.
  }
}

export function shouldSkipSecureSetupOnPortalRoutingPage(authoritiesLike, options = {}) {
  const pathname = typeof options?.pathname === 'string' ? options.pathname.trim() : ''
  const explicitlyChoseConsumerPortal = options?.explicitlyChoseConsumerPortal === true

  if (pathname !== '/' && pathname !== '') {
    return false
  }

  if (explicitlyChoseConsumerPortal) {
    return false
  }

  return Boolean(resolveDirectPortalHandoff(authoritiesLike, options))
}
